import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import {
  AppButton, SearchInput, EmptyState, ConfirmModal,
  StatsCard, StatsGrid, StatusBadge, Paginator, AppCard, AppCardContent,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import {
  listAdminBlogs, deleteBlog, updateBlog,
} from '../../utils/cmsAdminApi';
import './cms-admin.scss';

const PAGE_SIZE = 12;

const BlogsList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  const fetchBlogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await listAdminBlogs({
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      setBlogs(res.data || []);
      setPagination(res.pagination || { page: 1, total: 0, pages: 1 });
    } catch {
      showToast('Failed to load blogs', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchBlogs(1); }, [fetchBlogs]);

  const stats = {
    total: pagination.total,
    published: blogs.filter(b => b.status === 'published').length,
    drafts: blogs.filter(b => b.status === 'draft').length,
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteBlog(deleteId);
      showToast('Blog deleted');
      setDeleteId(null);
      fetchBlogs(pagination.page);
    } catch {
      showToast('Failed to delete blog', 'error');
    }
  };

  const togglePublish = async (blog) => {
    try {
      const next = blog.status === 'published' ? 'draft' : 'published';
      await updateBlog(blog._id, { status: next });
      showToast(next === 'published' ? 'Blog published' : 'Moved to draft');
      fetchBlogs(pagination.page);
    } catch {
      showToast('Failed to update blog', 'error');
    }
  };

  return (
    <div className="cms-admin-page">
      <StatsGrid cols={3} className="cms-admin-stats">
        <StatsCard title="Total" value={stats.total} icon={FileText} />
        <StatsCard title="Published" value={stats.published} icon={Eye} />
        <StatsCard title="Drafts" value={stats.drafts} icon={EyeOff} />
      </StatsGrid>

      <div className="cms-admin-toolbar">
        <SearchInput
          value={search}
          onChange={(v) => setSearch(v)}
          placeholder="Search blogs by title…"
        />
        <select
          className="cms-admin-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <AppButton variant="primary" className="ms-auto" onClick={() => navigate('/cms/blogs/new')}>
          <Plus size={16} /> New Blog
        </AppButton>
      </div>

      {loading ? (
        <div className="cms-admin-loading">Loading blogs…</div>
      ) : blogs.length === 0 ? (
        <EmptyState
          icon={<FileText size={40} />}
          title="No blogs yet"
          description="Create your first blog post to share with prospective students."
          action={
            <AppButton variant="primary" onClick={() => navigate('/cms/blogs/new')}>
              <Plus size={16} /> Create Blog
            </AppButton>
          }
        />
      ) : (
        <>
          <div className="cms-admin-grid">
            {blogs.map((b) => (
              <AppCard key={b._id} className="cms-admin-card">
                <AppCardContent>
                  <div className="cms-admin-card__header">
                    <h3 className="cms-admin-card__title">{b.title}</h3>
                    <StatusBadge status={b.status === 'published' ? 'success' : 'warning'}>
                      {b.status === 'published' ? 'Published' : 'Draft'}
                    </StatusBadge>
                  </div>
                  {b.excerpt && <p className="cms-admin-card__excerpt">{b.excerpt}</p>}
                  <div className="cms-admin-card__meta">
                    {b.author && <span>By {b.author}</span>}
                    {b.publishedAt && (
                      <span>{new Date(b.publishedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                  {Array.isArray(b.tags) && b.tags.length > 0 && (
                    <div className="cms-admin-card__tags">
                      {b.tags.slice(0, 4).map(t => <span key={t} className="cms-admin-tag">{t}</span>)}
                    </div>
                  )}
                  <div className="cms-admin-card__actions">
                    <AppButton size="sm" variant="ghost" onClick={() => togglePublish(b)}>
                      {b.status === 'published' ? <><EyeOff size={14}/> Unpublish</> : <><Eye size={14}/> Publish</>}
                    </AppButton>
                    <AppButton size="sm" variant="secondary" onClick={() => navigate(`/cms/blogs/${b._id}`)}>
                      <Edit size={14}/> Edit
                    </AppButton>
                    <AppButton size="sm" variant="danger" onClick={() => setDeleteId(b._id)}>
                      <Trash2 size={14}/>
                    </AppButton>
                  </div>
                </AppCardContent>
              </AppCard>
            ))}
          </div>

          {/* ⚠️ `page` / `onChange`, not `currentPage` / `onPageChange`.
              Paginator accepts neither of those names, so both were dropped:
              no page was ever marked current and clicking one did nothing. */}
          {pagination.pages > 1 && (
            <Paginator
              page={pagination.page}
              totalPages={pagination.pages}
              onChange={(p) => fetchBlogs(p)}
            />
          )}
        </>
      )}

      <ConfirmModal
        show={!!deleteId}
        title="Delete blog?"
        message="This will permanently remove the blog post from your public website."
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default BlogsList;
